import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { useNavigate, useParams } from 'react-router-dom';
import Breadcrumb from '../common/Breadcrumb';
import { parse, isValid, format, isAfter, isEqual } from 'date-fns';
import { CalendarIcon } from '@heroicons/react/24/outline';
import { useProjects } from '../../context/ProjectContext';
import { useCustomers } from '../../context/CustomerContext';
import { useLoading } from '../../context/LoadingContext';
import { useRoutePath } from '../../utils/getRoutePath';

const dateFormat = 'MM-dd-yyyy';
 
const schema = yup.object().shape({
  name: yup.string().required('Project name is required'),
  customer_id: yup.string().required('Customer is required'),
  start_date: yup.string()
    .required('Start date is required')
    .test('valid-MM-DD-YYYY', 'Date must be in MM-DD-YYYY format', function (value) {
      if (typeof value !== 'string') return false;

      const parsed = parse(value, dateFormat, new Date());

      return isValid(parsed) && format(parsed, dateFormat) === value;
    }),
    end_date: yup.string()
    .required('End date is required')
    .test('valid-end-date', 'End date must be in MM-DD-YYYY format', function (value) {
      if (!value) return false;
      const parsed = parse(value, dateFormat, new Date());
      return isValid(parsed) && format(parsed, dateFormat) === value;
    })
    .test('after-or-same-as-start', 'End date must be the same or after start date', function (endValue) {
      const { start_date } = this.parent;

      const start = parse(start_date, dateFormat, new Date());
      const end = parse(endValue, dateFormat, new Date());

      return (
        isValid(start) &&
        isValid(end) &&
        (isAfter(end, start) || isEqual(end, start))
      );
    }),
  description: yup.string().required('Description is required'),
  active: yup.boolean()
});

const ProjectForm = () => {
  const getRoutePath = useRoutePath();
  const navigate = useNavigate();
  const { id } = useParams();
  const { addProject, updateProject, getProjectById } = useProjects();
  const { getCustomers } = useCustomers();
  const [isLoading, setIsLoading] = useState(false);
  const { showLoading, hideLoading } = useLoading();
  const [customers, setCustomers] = useState([]);
  const dataLoadedRef = useRef(false);
  const hasFetched = useRef(false);
  const [getEditData, setGetEditData] = useState(null);

  const getDefaultValues = useCallback((existingData = null) => {
    const data = existingData || {};
    
    return {
      name: data?.name || '',
      customer_id: data?.customer_id || '',
      start_date: data?.start_date || '',
      end_date: data?.end_date || '',
      description: data?.description || '',
      active: data?.active ?? true
    };
  }, []);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    setError,
    reset,
    watch    
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: getDefaultValues(getEditData),
    mode: 'onChange'
  });

  // Reset form when not in edit mode
  useEffect(() => {
    if (!id) {
      reset(getDefaultValues());
    }
  }, [id, reset, getDefaultValues]);

  const fetchProjectData = useCallback(async () => {
    if (id && !hasFetched.current) {
      hasFetched.current = true;
      showLoading();
      try {
        const response = await getProjectById(id);
        if (response?.status === 200 && response?.data) {
          const project = response.data;
          const formData = {
            name: project.name,
            customer_id: project.customer_id,
            start_date: project.start_date?.split('T')[0],
            end_date: project.end_date?.split('T')[0],
            description: project.description,
            active: project.active ?? true
          };
          setGetEditData(formData);
          // Reset form with fetched data
          reset(getDefaultValues(formData));
        } else {
          toast.error('Project not found');
          navigate(getRoutePath('/projects'));
        }
      } catch (error) {
        console.error('Error fetching project data:', error);
        toast.error('Error fetching project data');
      } finally {
        hideLoading();
      }
    }
  }, [id, getProjectById, showLoading, hideLoading, reset, getDefaultValues, navigate, getRoutePath]);

  useEffect(() => {
    fetchProjectData();
  }, [fetchProjectData]);

  // Load customers data
  useEffect(() => {
    if (dataLoadedRef.current) return;
    
    const loadData = async () => {
      try {
        // Load customers
        const customersData = await getCustomers(1, { all: true });
        if (customersData) {
          setCustomers(customersData?.data);
        }
 
        dataLoadedRef.current = true;
      } catch (error) {
        console.error('Error loading data:', error);
        toast.error('Failed to load form data');
      }
    };

    loadData();
  }, [getCustomers]);



  const onSubmit = async (data) => {
    setIsLoading(true);
    showLoading();
    
    try {
      let response;
      
      if (id) {
        // Update existing project
        response = await updateProject(id, data);
      } else {
        // Create new project
        response = await addProject(data);
      }
      
      if (response.status === 200 || response.status === 201) {
        toast.success(id ? 'Project updated successfully!' : 'Project created successfully!');
        navigate(getRoutePath('/projects'));
      } else if (response.status === 422) {
        // Handle validation errors
        if (response.errors) {
          Object.keys(response.errors).forEach(field => {
            const errorMessage = response.errors[field][0];
            setError(field, { message: errorMessage });
            toast.error(`${field}: ${errorMessage}`);
          });
        }
      } else {
        toast.error('Error saving project');
      }
    } catch (error) {
      toast.error('Error saving project');
      console.error('Error:', error);
    } finally {
      setIsLoading(false);
      hideLoading();
    }
  };
  
  return (
    <div className="container mx-auto px-4 py-8">
      <Breadcrumb />
      <h1 className="text-2xl font-bold text-gray-900 mb-6">
          {id ? 'Edit Project' : 'Create New Project'}
        </h1>
      <div className="bg-white rounded-lg shadow-md p-6">
         
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6" autoComplete="off">
          <div>
            <label className="block text-sm font-medium text-gray-700">Project Name <span className='text-red-500'>*</span></label>
            <input
              type="text"
              {...register('name')}
              className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 border-gray-300 ${
                errors.name ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''
              }`}
              placeholder="Enter project name"
            />
            {errors.name && (
              <p className="mt-1 text-sm text-red-600">{errors.name.message}</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Customer <span className='text-red-500'>*</span></label>
            <select
              {...register('customer_id')}
              value={watch('customer_id') || ''}
              onChange={(e) => {
                const selectedId = Number(e.target.value);
                const selectedCustomer = customers.find(c => c.id === selectedId);
                
                setValue('customer_id', selectedId, { shouldValidate: true });

                // Optional: also set a separate `customer` field if needed
                if (selectedCustomer) {
                  setValue('customer', {
                    id: selectedCustomer.id,
                    name: selectedCustomer.name
                  }, { shouldValidate: true });
                }
              }}
              className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 border-gray-300 ${
                errors.customer_id ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''
              }`}
            >
              <option value="">Select a customer</option>
              {customers.map((customer) => (
                <option key={customer.id} value={customer.id}>
                  {customer.name}
                </option>
              ))}
            </select>

            {errors.customer_id && (
              <p className="mt-1 text-sm text-red-600">{errors.customer_id.message}</p>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        
            <div>
              <label className="block text-sm font-medium text-gray-700">Start Date <span className='text-red-500'>*</span></label>

              <div className="relative">
                <input
                  type="text"
                  {...register('start_date')}
                  className={`datepicker w-full pr-10 px-4 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 border-gray-300 ${
                    errors.start_date ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''
                  }`}
                  placeholder="Enter start date"
                  
                  onInput={(e) => {
                    setValue('start_date', e.target.value, { shouldValidate: true });
                  }}
                  
                />
                
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                  <CalendarIcon className="w-5 h-5 text-gray-400" />
                </div>
              </div>

              {errors.start_date && (
                <p className="mt-1 text-sm text-red-600">{errors.start_date.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">End Date <span className='text-red-500'>*</span></label>

              <div className="relative">
                <input
                  type="text"
                  {...register('end_date')}
                  className={`datepicker w-full pr-10 px-4 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 border-gray-300 ${
                    errors.end_date ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''
                  }`}
                  placeholder="Enter end date"
                  
                  onInput={(e) => {
                    setValue('end_date', e.target.value, { shouldValidate: true });
                  }}
                />
                
                <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                  <CalendarIcon className="w-5 h-5 text-gray-400" />
                </div>
              </div>

              {errors.end_date && (
                <p className="mt-1 text-sm text-red-600">{errors.end_date.message}</p>
              )}
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">Description <span className='text-red-500'>*</span></label>
            <textarea
              {...register('description')}
              rows={4}
              className={`w-full px-4 py-2 border rounded-md focus:ring-2 focus:ring-blue-500 focus:border-blue-500 border-gray-300 ${
                errors.description ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''
              }`}
              placeholder="Enter project description"
            />
            {errors.description && (
              <p className="mt-1 text-sm text-red-600">{errors.description.message}</p>
            )}
          </div>

          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <input
                type="checkbox"
                {...register('active')}
                id="active"
                className="h-4 w-4 text-[#009efb] focus:ring-[#009efb] border-gray-300 rounded"
              />
              <label htmlFor="active" className="ml-2 block text-sm text-gray-900">
                Active
              </label>
            </div>
          </div>

          <div className="flex justify-end">
            <button
              type="submit"
              disabled={isLoading}
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-[#009efb] hover:bg-[#0089db] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#009efb] transition-colors duration-200"
            >
              {isLoading ? 'Saving...' : (id ? 'Update Project' : 'Create Project')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ProjectForm; 